import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import { setHeaderTabsConfig } from '@app/actions/headerActions';
import { closeHelp } from '@app/actions/helpActions';
import { isStickerFlow, isSheetFlow, isApplyCodesViewAvailable, isMobileMode, isMultiUpload } from '@app/utils/utils';
import { UPLOAD_ART_VIEW, CUSTOMIZE_VIEW, WHITE_INK_OPTIONS_VIEW, ERROR_MODE_VIEW, APPLY_CODES_VIEW } from '@app/constants';

import NavTabIndicator from '@components/header/nav-tab-indicator/NavTabIndicator';
import NavTabsGroup from '@components/header/nav-tabs-group/NavTabsGroup';
import HelpToggler from '@components/help/help-toggler/HelpToggler';
import './Header.scss';

const Header = ({
  currentView,
  isMobileView,
  isSticker,
  isSheet,
  isApplyCodes,
  isMultiUpload,
  whiteInkOptionsNeeded,
  setHeaderTabsConfig,
  closeHelp,
  isHelpActive,
  isErrorMode,
}) => {
  useEffect(() => {
    const prevView = sessionStorage.getItem('prevView');
    const isActive = view => [currentView, prevView].includes(view);
    setHeaderTabsConfig([
      {
        id: 'upload-art-tab',
        hidden: isErrorMode,
        active: currentView === UPLOAD_ART_VIEW,
        locKey: 'qau-header-title-upload-art',
      },
      {
        id: 'customize-art-tab',
        hidden: isErrorMode || isMultiUpload,
        active: isActive(CUSTOMIZE_VIEW),
        locKey: `qau-title-${isSticker || isSheet ? 'customize-art-tab-sticker' : 'art-position'}`,
      },
      {
        id: 'white-options-tab',
        hidden: !whiteInkOptionsNeeded || isErrorMode,
        active: isActive(WHITE_INK_OPTIONS_VIEW),
        locKey: 'qau-title-white-options',
      },
      {
        id: 'apply-codes-tab',
        hidden: !isApplyCodes || isErrorMode,
        active: isActive(APPLY_CODES_VIEW),
        locKey: 'qau-title-apply-codes',
      },
    ]);
  }, [currentView, isApplyCodes, isSticker, isSheet, isErrorMode, isMultiUpload, setHeaderTabsConfig, whiteInkOptionsNeeded]);

  const closeHelpIfNeeded = () => {
    isHelpActive && closeHelp();
  };

  const onHeaderClick = () => {
    closeHelpIfNeeded();
  };

  return (
    <div
      id="qau-header"
      data-testid="qau-header"
      className={classnames({ mobile: isMobileView })}
      onClick={onHeaderClick}
      onKeyDown={e => e.key === 'Enter' && onHeaderClick()}
    >
      {isMobileView ? <NavTabIndicator /> : <NavTabsGroup />}
      {currentView !== ERROR_MODE_VIEW && <HelpToggler className="help-toggler" />}
    </div>
  );
};

const mapStateToProps = ({ matrix, whiteInkOptions, help }, { flowConfig = {} }) => {
  const { ctx, view: currentView } = matrix;
  const { whiteInkOptionsNeeded = false } = whiteInkOptions || {};

  return {
    currentView,
    isMobileView: isMobileMode(ctx),
    isSticker: isStickerFlow(flowConfig),
    isSheet: isSheetFlow(flowConfig),
    isApplyCodes: isApplyCodesViewAvailable(flowConfig),
    isMultiUpload: isMultiUpload(flowConfig),
    isErrorMode: currentView === ERROR_MODE_VIEW,
    whiteInkOptionsNeeded,
    isHelpActive: !!help.activated,
  };
};

const mapDispatchToProps = {
  setHeaderTabsConfig,
  closeHelp,
};

Header.propTypes = {
  currentView: PropTypes.string.isRequired,
  isMobileView: PropTypes.bool.isRequired,
  isSticker: PropTypes.bool.isRequired,
  isSheet: PropTypes.bool.isRequired,
  isApplyCodes: PropTypes.bool.isRequired,
  isMultiUpload: PropTypes.bool.isRequired,
  isErrorMode: PropTypes.bool.isRequired,
  isHelpActive: PropTypes.bool.isRequired,
  whiteInkOptionsNeeded: PropTypes.bool,
  setHeaderTabsConfig: PropTypes.func.isRequired,
  closeHelp: PropTypes.func.isRequired,
};

export default inject('flowConfig')(connect(mapStateToProps, mapDispatchToProps)(Header));
